import type { ReactNode } from "react";
import {
  AtSign,
  CalendarDays,
  FileBarChart,
  KeyRound,
  Lock,
  MessageCircle,
  Palette,
  Pencil,
  StickyNote,
  UserRound,
} from "lucide-react";
import {
  ShowBase,
  useCanAccess,
  useGetOne,
  useLocaleState,
  useShowContext,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Client, Sale } from "../types";
import { BrandKit } from "../client-files/BrandKit";
import { ClientReports } from "../client-files/ClientReports";
import { ClientContractCard } from "./ClientContractCard";
import { PortalAccessCard } from "./PortalAccessCard";
import { formatDateOnly, getPlatformLabel } from "./clientModel";

export const ClientShow = () => (
  <ShowBase>
    <ClientShowContent />
  </ShowBase>
);

const ClientShowContent = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { record, isPending } = useShowContext<Client>();
  const { companySectors } = useConfigurationContext();
  // Administrators publish reports of any area; the team only content reports.
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "client_contracts",
    action: "edit",
  });
  if (isPending || !record) return null;

  const sectorLabel = companySectors.find(
    (sector) => sector.value === record.sector,
  )?.label;

  return (
    <div className="mt-6 flex flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-center gap-4">
        <CompanyAvatar />
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight">
            {record.name}
          </h1>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {sectorLabel ? <span>{sectorLabel}</span> : null}
            {record.client_since ? (
              <span className="flex items-center gap-1.5">
                <CalendarDays className="size-4" />
                {translate("resources.clients.fields.client_since")}{" "}
                {formatDateOnly(record.client_since, locale)}
              </span>
            ) : null}
          </div>
        </div>
        <Link
          to={`/clients/${record.id}`}
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground no-underline hover:opacity-90"
        >
          <Pencil className="size-4" />
          {translate("resources.clients.action.edit")}
        </Link>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-4">
          <ServiceCard client={record} />
          <ContactCard client={record} />
          <AccountsCard client={record} />
          <NotesCard client={record} />
        </div>
        <div className="flex flex-col gap-4">
          <ClientContractCard companyId={record.id} />
          <PortalAccessCard
            companyId={record.id}
            whatsapp={record.main_contact_whatsapp}
          />
        </div>
      </div>

      <Section
        icon={<FileBarChart className="size-4" />}
        title={translate("resources.client_reports.title")}
      >
        <ClientReports
          companyId={record.id}
          manager={isAdmin ? "admin" : "team"}
        />
      </Section>
      <Section
        icon={<Palette className="size-4" />}
        title={translate("resources.brand_assets.title")}
      >
        <BrandKit companyId={record.id} canManage />
      </Section>
    </div>
  );
};

export const Section = ({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) => (
  <section className="rounded-[22px] bg-card p-5">
    <h2 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {icon}
      {title}
    </h2>
    {children}
  </section>
);

const Field = ({ label, value }: { label: string; value?: ReactNode }) => {
  const translate = useTranslate();
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate">
        {value || (
          <span className="text-muted-foreground">
            {translate("resources.clients.not_set")}
          </span>
        )}
      </div>
    </div>
  );
};

const ServiceCard = ({ client }: { client: Client }) => {
  const translate = useTranslate();
  const { dealCategories } = useConfigurationContext();
  const { data: lead } = useGetOne<Sale>(
    "sales",
    { id: client.sales_id ?? 0 },
    { enabled: client.sales_id != null },
  );

  return (
    <Section
      icon={<KeyRound className="size-4" />}
      title={translate("resources.clients.sections.service")}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={translate("resources.clients.fields.service_mode")}
          value={
            client.service_mode
              ? translate(
                  `resources.clients.service_modes.${client.service_mode}`,
                )
              : null
          }
        />
        <Field
          label={translate("resources.clients.fields.sales_id")}
          value={lead ? `${lead.first_name} ${lead.last_name}` : null}
        />
      </div>
      <div className="mt-4">
        <div className="text-xs text-muted-foreground">
          {translate("resources.clients.fields.services")}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {client.services.length === 0 ? (
            <span className="text-muted-foreground">
              {translate("resources.clients.not_set")}
            </span>
          ) : (
            client.services.map((value) => (
              <span
                key={value}
                className="rounded-full bg-primary/15 px-3 py-1 text-sm text-primary"
              >
                {dealCategories.find((c) => c.value === value)?.label ?? value}
              </span>
            ))
          )}
        </div>
      </div>
    </Section>
  );
};

const ContactCard = ({ client }: { client: Client }) => {
  const translate = useTranslate();
  const whatsappDigits = client.main_contact_whatsapp?.replace(/\D/g, "");

  return (
    <Section
      icon={<UserRound className="size-4" />}
      title={translate("resources.clients.sections.contact")}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label={translate("resources.clients.fields.main_contact_name")}
          value={client.main_contact_name}
        />
        <Field
          label={translate("resources.clients.fields.main_contact_role")}
          value={client.main_contact_role}
        />
        <Field
          label={translate("resources.clients.fields.main_contact_whatsapp")}
          value={
            whatsappDigits ? (
              <a
                href={`https://wa.me/${whatsappDigits}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-primary"
              >
                <MessageCircle className="size-4" />
                {client.main_contact_whatsapp}
              </a>
            ) : null
          }
        />
        <Field
          label={translate("resources.clients.fields.main_contact_email")}
          value={
            client.main_contact_email ? (
              <a
                href={`mailto:${client.main_contact_email}`}
                className="inline-flex items-center gap-1.5 text-primary"
              >
                <AtSign className="size-4" />
                {client.main_contact_email}
              </a>
            ) : null
          }
        />
        <Field
          label={translate("resources.clients.fields.content_approver")}
          value={client.content_approver}
        />
      </div>
    </Section>
  );
};

const AccountsCard = ({ client }: { client: Client }) => {
  const translate = useTranslate();
  return (
    <Section
      icon={<Lock className="size-4" />}
      title={translate("resources.clients.sections.accounts")}
    >
      {client.managed_accounts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.clients.accounts.empty")}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border">
          {client.managed_accounts.map((account, index) => (
            <li
              key={`${account.platform}-${index}`}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5 first:pt-0 last:pb-0"
            >
              <span className="font-medium">
                {getPlatformLabel(account.platform)}
              </span>
              {account.handle ? (
                <span className="text-muted-foreground">{account.handle}</span>
              ) : null}
              {account.notes ? (
                <span className="w-full text-sm text-muted-foreground">
                  {account.notes}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

const NotesCard = ({ client }: { client: Client }) => {
  const translate = useTranslate();
  return (
    <Section
      icon={<StickyNote className="size-4" />}
      title={translate("resources.clients.sections.notes")}
    >
      {client.client_notes ? (
        <p className="whitespace-pre-line leading-relaxed">
          {client.client_notes}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("resources.clients.not_set")}
        </p>
      )}
    </Section>
  );
};
